import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Row,
  Col,
  Statistic,
  InputNumber,
} from 'antd';
import {
  HomeOutlined,
  ClockCircleOutlined,
  EditOutlined,
  SendOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STATUS_MAP = {
  provider_assigned: { color: 'purple', label: 'Assigned' },
  provider_submitted: { color: 'blue', label: 'Submitted' },
};

const calculateYearlyPrice = (points) => {
  if (!points || points <= 0) return 0;
  const monthly = 250 + Math.floor(points / 100) * 50;
  return monthly * 12;
};

const formatNPR = (amount) => {
  if (!amount || amount <= 0) return '-';
  return 'Rs. ' + Number(amount).toLocaleString('en-IN');
};

const AssessmentTasks = () => {
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submitForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery(
    'assessment-tasks',
    async () => {
      const response = await api.get('/provider/membership/tasks');
      return response.data?.data || [];
    },
    {
      refetchInterval: 30000,
      retry: 1,
    },
  );

  const { data: tiers = [] } = useQuery('pricing-tiers', async () => {
    const response = await api.get('/membership/tiers');
    return response.data?.data || [];
  });

  const submitMutation = useMutation(
    async ({ id, values }) => {
      const response = await api.post(`/provider/membership/tasks/${id}/submit`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Assessment submitted successfully.');
        setIsSubmitModalVisible(false);
        setSelectedTask(null);
        submitForm.resetFields();
        queryClient.invalidateQueries('assessment-tasks');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to submit');
      },
    },
  );

  const assignedTasks = tasks.filter((t) => t.status === 'provider_assigned');
  const submittedTasks = tasks.filter((t) => t.status === 'provider_submitted');

  const previewPrice = (points) => {
    if (!points || points <= 0) return null;
    return formatNPR(calculateYearlyPrice(points));
  };

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.customer_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.customer_phone}
          </Text>
          {record.customer_address && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.customer_address}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'assessment_points',
      key: 'points',
      render: (points) =>
        points > 0 ? <Tag color="blue">{points} pts</Tag> : <Tag color="default">Not assessed</Tag>,
    },
    {
      title: 'Est. Yearly Price',
      dataIndex: 'yearly_price',
      key: 'price',
      render: (price) =>
        price > 0 ? (
          <div>
            <div>{formatNPR(price)}/yr</div>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const meta = STATUS_MAP[status] || { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date) => (date ? dayjs(date).format('MMM D, YYYY') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          {record.status === 'provider_assigned' && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedTask(record);
                submitForm.setFieldsValue({
                  assessment_points: record.assessment_points || 0,
                  provider_notes: record.provider_notes || '',
                });
                setIsSubmitModalVisible(true);
              }}
            >
              Submit Assessment
            </Button>
          )}
          {record.status === 'provider_submitted' && (
            <Text type="secondary">Waiting for admin</Text>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Assessment Tasks</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic title="Total Tasks" value={tasks.length} prefix={<HomeOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic
              title="Assigned"
              value={assignedTasks.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic
              title="Submitted"
              value={submittedTasks.length}
              prefix={<SendOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Text strong>
              <DollarOutlined /> Pricing Reference — Yearly Price = (250 + floor(points/100) × 50) ×
              12
            </Text>
          </Col>
        </Row>
        <Table
          dataSource={tiers}
          columns={[
            {
              title: 'Points Range',
              key: 'range',
              render: (_, r) =>
                r.max_points ? `${r.min_points} - ${r.max_points}` : `${r.min_points}+`,
              width: 120,
            },
            {
              title: 'Monthly',
              key: 'monthly',
              render: (_, r) => `Rs. ${r.monthly_price}`,
              width: 100,
            },
            {
              title: 'Yearly Price',
              key: 'yearly',
              render: (_, r) => (
                <Text strong>Rs. {Number(r.yearly_price).toLocaleString('en-IN')}</Text>
              ),
              width: 120,
            },
          ]}
          rowKey="min_points"
          pagination={false}
          size="small"
          showHeader={false}
          bordered
        />
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={tasks}
          loading={isLoading}
          rowKey="id"
          locale={{ emptyText: 'No assessment tasks assigned to you yet.' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} tasks`,
          }}
        />
      </Card>

      <Modal
        title="Submit Assessment"
        open={isSubmitModalVisible}
        onCancel={() => {
          setIsSubmitModalVisible(false);
          submitForm.resetFields();
        }}
        footer={null}
        width={520}
      >
        <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
          <Row>
            <Col span={24}>
              <Text strong>Customer: </Text>
              <Text>{selectedTask?.customer_name}</Text>
            </Col>
            <Col span={24}>
              <Text strong>Phone: </Text>
              <Text>{selectedTask?.customer_phone}</Text>
            </Col>
            <Col span={24}>
              <Text strong>Address: </Text>
              <Text>{selectedTask?.customer_address}</Text>
            </Col>
          </Row>
        </div>

        <Form
          form={submitForm}
          layout="vertical"
          onFinish={(values) => {
            submitMutation.mutate({ id: selectedTask.id, values });
          }}
        >
          <Form.Item
            name="assessment_points"
            label="Assessment Points"
            rules={[{ required: true, message: 'Enter assessment points' }]}
            tooltip="Rate the property condition. Yearly price = (250 + floor(points/100) × 50) × 12"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 250" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.assessment_points !== curr.assessment_points}
          >
            {({ getFieldValue }) => {
              const pts = getFieldValue('assessment_points');
              return pts > 0 ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '8px 12px',
                    background: '#f6ffed',
                    borderRadius: 4,
                  }}
                >
                  <Text>
                    Estimated yearly price: <Text strong>{previewPrice(pts)}/yr</Text>
                  </Text>
                </div>
              ) : null;
            }}
          </Form.Item>

          <Form.Item name="provider_notes" label="Provider Notes">
            <Input.TextArea
              rows={3}
              placeholder="Describe the property condition, appliances, and assessment details..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitMutation.isLoading}
                icon={<SendOutlined />}
              >
                Submit Assessment
              </Button>
              <Button
                onClick={() => {
                  setIsSubmitModalVisible(false);
                  submitForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssessmentTasks;
